(function () {
  'use strict';

  var _s = window.__ddCartShared = window.__ddCartShared || {};
  _s.granularFooterPending = false;
  /* Treat the SSR-rendered drawer as fresh on page load. The Liquid
     render of cart-drawer.liquid IS a section render — it's the same
     HTML the /cart?sections=cart-drawer endpoint would return for
     this state. There's no value in re-fetching it on the first cart
     icon click. The 60s freshness window means subsequent clicks
     within a minute also skip the fetch. After that, the fetch +
     painter run again as a cross-tab safety net.

     Use `||` so we don't overwrite a real timestamp if the script is
     re-evaluated after a section swap (which happens when a section
     containing this asset link gets re-included). The initial value
     wins; subsequent painter runs update it via the legitimate path
     in `patchDrawerFromSection`. */
  if (!_s.sectionFooterFreshTs) _s.sectionFooterFreshTs = Date.now();

  var SHIMMER_MS = 320;
  var CUSTOM_INFO_EXIT_MS = 240;
  var XSELL_COLLAPSE_MS = 400;
  var _drawerItemsSwapToken = 0;
  var _sectionReplaceInProgress = false;
  var _sectionReplaceQueue = [];
  var _pendingDrawerSwapPromise = Promise.resolve();

  function metrics() { return window.__ddCartMetrics || {}; }
  function facade() { return window.ddCartHelpers || {}; }
  function debugLog(name, data) { var f = facade(); if (f.debugLog) f.debugLog(name, data); }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function queue() { return window._ddCartQueue || {}; }

  function getActiveLineAction() {
    var q = queue();
    return q.getActiveLineJob ? q.getActiveLineJob() : null;
  }

  /* Tier preview formatter — shared with dd-shipping-protection.js so cart-paint
     can stamp the price from Liquid's data-dd-protection-preview-cents without
     waiting on a cached/stale protection module (Chrome disk-cache bug). */
  function formatProtectionPreviewCents(cents) {
    var n = Number(cents);
    if (!isFinite(n) || n <= 0) return '+$0';
    var fmt = (window.shopMoneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    var amount = (n / 100).toFixed(2);
    var amountWithCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = fmt
      .replace(/\{\{\s*amount\s*\}\}/, amountWithCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(n / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(n / 100).toString());
    return '+' + out.replace(/([.,])00(?!\d)/, '');
  }

  function applyProtectionPreviewFromWidget(widget) {
    if (!widget || !widget.getAttribute) return;
    var raw = widget.getAttribute('data-dd-protection-preview-cents');
    if (raw == null || raw === '') return;
    var cents = parseInt(raw, 10);
    if (!isFinite(cents)) return;
    var priceEl = widget.querySelector('[data-dd-protection-price]');
    if (priceEl) priceEl.textContent = formatProtectionPreviewCents(cents);
  }

  window.__ddFormatProtectionPreviewCents = formatProtectionPreviewCents;
  window.__ddApplyProtectionPreviewFromWidget = applyProtectionPreviewFromWidget;

  function withLocalShimmer(el, fn) {
    if (!el) { if (fn) fn(); return; }
    if (prefersReducedMotion()) { fn(); return; }
    el.classList.add('dd-mutate-shimmer');
    try { fn(); } catch (err) { console.error('[cart-paint] withLocalShimmer', err); }
    window.setTimeout(function () { el.classList.remove('dd-mutate-shimmer'); }, SHIMMER_MS);
  }

  function rehydrateBogosVisibility() {
    if (typeof window._ddBogosInlineGiftRender === 'function') window._ddBogosInlineGiftRender();
  }

  /* BOGOS gift-slot visibility flags (data-dd-bogos-qualified /
     threshold-met / has-gift) are Liquid-owned state carried in section
     HTML. The granular settle path skips wholesale innerHTML swaps, so
     without this sync the live slot's flags freeze at whatever the last
     full swap rendered — once the card animated out it could never come
     back until a structural change forced a full swap. Copy the fresh
     flags across and drop the render key so dd-cart-gift re-renders. */
  function syncBogosSlotAttributes(liveSlot, nextSlot) {
    if (!liveSlot || !nextSlot) return;
    var changed = false;
    var attrs = nextSlot.attributes;
    for (var i = 0; i < attrs.length; i++) {
      var name = attrs[i].name;
      if (name.indexOf('data-dd-') !== 0) continue;
      if (name === 'data-dd-bogos-render-key') continue;
      if (liveSlot.getAttribute(name) !== attrs[i].value) {
        liveSlot.setAttribute(name, attrs[i].value);
        changed = true;
      }
    }
    if (changed) liveSlot.removeAttribute('data-dd-bogos-render-key');
  }

  /* ----------------------------------------------------------------
   * Cart-page footer capture / restore around full-section swap
   * --------------------------------------------------------------
   * `replaceCartSections` does a wholesale `target.innerHTML = html`
   * for `#main-cart-footer .js-contents` (no granular patcher
   * exists for the cart-page footer the way `patchDrawerFromSection`
   * does for the drawer). That swap fires on every cart mutation —
   * including the ones the shopper just initiated (apply discount,
   * save note, qty change) — and rebuilds the entire footer subtree
   * from fresh SSR. Anything the shopper-side JS just wrote into
   * the footer DOM is lost:
   *
   *   - Open/closed state of the discount + note accordions
   *     (`is--open` class, `aria-expanded`).
   *   - Inline `.dd-discount__feedback` success / warn messages
   *     (set by `dd-discount-code.js` showFeedback).
   *   - The `[data-dd-cart-toast]` host's `is--visible` + content
   *     (set by `cart-toast.js` showInlineToast).
   *   - In-progress input text the shopper hasn't submitted yet
   *     (discount input, note input).
   *   - The note input's `data-dd-initial-note` baseline used by
   *     the unsaved-note checkout guard.
   *
   * Capture before the swap, restore right after. Anything we don't
   * recognize falls through to the SSR default (the right answer
   * for any DOM the section template owns).
   * --------------------------------------------------------------*/
  function captureAccordionState(root, selector) {
    var acc = root.querySelector(selector);
    if (!acc) return null;
    var toggle = acc.querySelector('[data-dd-accordion-toggle]');
    return {
      isOpen: acc.classList.contains('is--open'),
      ariaExpanded: toggle ? toggle.getAttribute('aria-expanded') : null
    };
  }

  function restoreAccordionState(root, selector, state) {
    if (!state) return;
    var acc = root.querySelector(selector);
    if (!acc) return;
    if (state.isOpen) acc.classList.add('is--open');
    var toggle = acc.querySelector('[data-dd-accordion-toggle]');
    if (toggle && state.ariaExpanded != null) toggle.setAttribute('aria-expanded', state.ariaExpanded);
  }

  function captureCartPageFooterEphemera(target) {
    if (!target) return null;
    var snap = {};

    snap.discountAccordion = captureAccordionState(target, '[data-dd-discount-accordion]');
    snap.noteAccordion     = captureAccordionState(target, '[data-dd-note-accordion]');

    var feedback = target.querySelector('.dd-discount__feedback');
    if (feedback && feedback.classList.contains('is--visible')) {
      snap.feedback = {
        html:      feedback.innerHTML,
        className: feedback.className
      };
    }

    var toast = target.querySelector('[data-dd-cart-toast]');
    if (toast && toast.classList.contains('is--visible')) {
      var msgEl  = toast.querySelector('[data-dd-cart-toast-msg]');
      var iconEl = toast.querySelector('[data-dd-cart-toast-icon]');
      snap.toast = {
        className: toast.className,
        msg:       msgEl  ? msgEl.textContent : '',
        iconHtml:  iconEl ? iconEl.innerHTML  : ''
      };
    }

    var discountInput = target.querySelector('[data-dd-discount-input]');
    if (discountInput && discountInput.value) snap.discountInputValue = discountInput.value;

    var noteInput = target.querySelector('[data-dd-note-input]');
    if (noteInput) {
      snap.noteInputValue       = noteInput.value || '';
      snap.noteInputInitialNote = noteInput.getAttribute('data-dd-initial-note') || '';
    }

    return snap;
  }

  function restoreCartPageFooterEphemera(target, snap) {
    if (!target || !snap) return;

    restoreAccordionState(target, '[data-dd-discount-accordion]', snap.discountAccordion);
    restoreAccordionState(target, '[data-dd-note-accordion]',     snap.noteAccordion);

    if (snap.feedback) {
      var feedback = target.querySelector('.dd-discount__feedback');
      if (feedback) {
        feedback.innerHTML = snap.feedback.html;
        feedback.className = snap.feedback.className;
      }
    }

    if (snap.toast) {
      var toast = target.querySelector('[data-dd-cart-toast]');
      if (toast) {
        toast.className = snap.toast.className;
        var msgEl  = toast.querySelector('[data-dd-cart-toast-msg]');
        var iconEl = toast.querySelector('[data-dd-cart-toast-icon]');
        if (msgEl)  msgEl.textContent  = snap.toast.msg;
        if (iconEl) iconEl.innerHTML   = snap.toast.iconHtml;
      }
    }

    /* Only restore in-progress input if the shopper has unsubmitted text;
       skip when the freshly-rendered SSR value already matches (avoids
       clobbering the canonical "applied" state with stale input text). */
    if (snap.discountInputValue) {
      var discountInput = target.querySelector('[data-dd-discount-input]');
      if (discountInput && !discountInput.value) discountInput.value = snap.discountInputValue;
    }

    if (snap.noteInputValue || snap.noteInputInitialNote) {
      var noteInput = target.querySelector('[data-dd-note-input]');
      if (noteInput) {
        if (snap.noteInputValue && noteInput.value !== snap.noteInputValue) {
          /* Unsaved edit — keep the shopper's draft text */
          noteInput.value = snap.noteInputValue;
        }
        /* The SSR baseline is the just-saved note (canonical). Keep it
           if the shopper has no unsaved draft; otherwise restore the
           captured baseline so the unsaved-note guard still trips. */
        if (snap.noteInputValue && snap.noteInputValue !== snap.noteInputInitialNote) {
          noteInput.setAttribute('data-dd-initial-note', snap.noteInputInitialNote);
        }
      }
    }
  }

  /* ----------------------------------------------------------------
   * dd-cart:items-swapped
   * --------------------------------------------------------------
   * Stable post-swap signal for any surface (cross-sell hydration,
   * variant-map augmentation, third-party widgets) that needs to act
   * on the FRESH cart DOM after a section replacement.
   *
   * Why this exists: cart-queue.js publishes PUB_SUB_EVENTS.cartUpdate
   * immediately after replaceCartSections() returns, but the actual
   * innerHTML swap inside patchDrawerFromSection can be deferred up
   * to XSELL_COLLAPSE_MS / CUSTOM_INFO_EXIT_MS to play exit
   * animations. cartUpdate subscribers therefore run against STALE
   * pre-swap DOM and any post-swap mutations they make are lost when
   * the deferred innerHTML assignment overwrites them. This event is
   * dispatched ONLY after the actual DOM swap, so subscribers get a
   * truthful "your DOM is fresh now" signal regardless of timing.
   *
   * Scope values:
   *   - 'drawer-items'  → CartDrawer-Items innerHTML replaced
   *   - 'drawer-root'   → entire #CartDrawer innerHTML replaced
   *   - 'cart-page'     → main-cart-items / main-cart-footer js-contents replaced
   * --------------------------------------------------------------*/
  function emitItemsSwapped(scope) {
    try {
      document.dispatchEvent(new CustomEvent('dd-cart:items-swapped', {
        detail: { scope: scope || 'unknown', ts: Date.now() }
      }));
    } catch (e) { /* CustomEvent unsupported — silently no-op */ }
  }

  function waitForDrawerSwapIdle(timeoutMs) {
    var timeout = typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : 1800;
    return Promise.resolve(_pendingDrawerSwapPromise).then(function () {
      return new Promise(function (resolve) {
        var started = Date.now();
        (function poll() {
          if (!_sectionReplaceInProgress && _sectionReplaceQueue.length === 0) return resolve();
          if (Date.now() - started >= timeout) return resolve();
          window.setTimeout(poll, 25);
        })();
      });
    });
  }

  function replaceDrawerInnerHTML(drawerInner, freshHTML) {
    if (!drawerInner || !freshHTML) return;
    _s.granularFooterPending = false;
    drawerInner.innerHTML = freshHTML;
    rehydrateBogosVisibility();
    emitItemsSwapped('drawer-inner');
  }

  function replaceCartDrawerRootHTML(cartDrawerRoot, freshInnerHTML) {
    if (!cartDrawerRoot || !freshInnerHTML) return;
    _s.granularFooterPending = false;
    cartDrawerRoot.innerHTML = freshInnerHTML;
    rehydrateBogosVisibility();
    emitItemsSwapped('drawer-root');
  }

  function parseCartDrawerRootInnerHtml(rawDrawerSectionHtml) {
    if (!rawDrawerSectionHtml) return '';
    var doc = new DOMParser().parseFromString(rawDrawerSectionHtml, 'text/html');
    var root = doc.querySelector('cart-drawer #CartDrawer') || doc.querySelector('#CartDrawer');
    return root ? root.innerHTML : '';
  }

  /* True when a MONEY row is guest-gated ("Login to see price" markup inside
     a price row). With the Wave A dual-render markup the login affordance
     lives in its own .dd-cart-item__login-row (never a price-row), so this
     returns false for healthy dual-render HTML in any session — it only
     fires on legacy single-branch guest HTML (pre-Wave-A caches). */
  function shellHasGuestGatedMoneyRows(shell) {
    if (!shell) return false;
    var rows = shell.querySelectorAll('.dd-cart-item__price-row');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].classList.contains('dd-cart-item__login-row')) continue;
      if (
        rows[i].querySelector('.dd-cart-item__login-price') ||
        /login to see price/i.test(rows[i].textContent || '')
      ) return true;
    }
    return false;
  }

  /* Granular settle: when a line transaction is active and the incoming
     section HTML has the SAME line structure as the live drawer (same keys,
     same order, same custom-card/group counts), patch only the volatile
     parts (qty value, money row, line indices, ship meta) instead of a
     wholesale innerHTML swap. Keeps focus, scroll, xsell expansion, and
     avoids re-running observers on identical structure. Structure changes
     (add/remove/gift/protection lines) always fall through to the swap. */
  function tryGranularItemsSettle(liveShell, nextItemsHtml) {
    try {
      var doc = new DOMParser().parseFromString('<div id="dd-settle-root"></div>', 'text/html');
      var root = doc.getElementById('dd-settle-root');
      if (!root) return false;
      root.innerHTML = nextItemsHtml;
      var liveRows = liveShell.querySelectorAll('[data-dd-cart-item]');
      var nextRows = root.querySelectorAll('[data-dd-cart-item]');
      if (!liveRows.length || liveRows.length !== nextRows.length) return false;
      for (var i = 0; i < liveRows.length; i++) {
        if ((liveRows[i].getAttribute('data-line-key') || '') !== (nextRows[i].getAttribute('data-line-key') || '')) return false;
      }
      if (liveShell.querySelectorAll('.dd-custom-card').length !== root.querySelectorAll('.dd-custom-card').length) return false;
      if (liveShell.querySelectorAll('.dd-item-group').length !== root.querySelectorAll('.dd-item-group').length) return false;

      for (var r = 0; r < liveRows.length; r++) {
        var live = liveRows[r];
        var next = nextRows[r];

        var liveInput = live.querySelector('[data-dd-qty-input]');
        var nextInput = next.querySelector('[data-dd-qty-input]');
        if (liveInput && nextInput && liveInput.tagName === 'INPUT' && document.activeElement !== liveInput) {
          var v = nextInput.getAttribute('value');
          if (v != null && v !== '') { liveInput.value = v; liveInput.setAttribute('value', v); }
        } else if (liveInput && nextInput && liveInput.tagName !== 'INPUT') {
          if (liveInput.textContent !== nextInput.textContent) liveInput.textContent = nextInput.textContent;
        }

        var liveMoney = findMoneyRow(live);
        var nextMoney = findMoneyRow(next);
        if (liveMoney && nextMoney && liveMoney.innerHTML !== nextMoney.innerHTML) {
          liveMoney.innerHTML = nextMoney.innerHTML;
        }

        var nextLine = next.getAttribute('data-line');
        if (nextLine && live.getAttribute('data-line') !== nextLine) {
          live.setAttribute('data-line', nextLine);
          var lined = live.querySelectorAll('[data-line]');
          for (var ll = 0; ll < lined.length; ll++) lined[ll].setAttribute('data-line', nextLine);
        }

        var livePo = live.querySelector('[data-dd-preorder-ship], [data-dd-ship-countdown]');
        var nextPo = next.querySelector('[data-dd-preorder-ship], [data-dd-ship-countdown]');
        if (livePo && nextPo && livePo.textContent !== nextPo.textContent) {
          livePo.textContent = nextPo.textContent;
        }
      }

      /* Gift slot flags must track the incoming section HTML even on a
         granular settle (see syncBogosSlotAttributes). Slot count mismatch
         means structure changed — fall through to the full swap. */
      var liveSlots = liveShell.querySelectorAll('[data-dd-bogos-slot]');
      var nextSlots = root.querySelectorAll('[data-dd-bogos-slot]');
      if (liveSlots.length !== nextSlots.length) return false;
      for (var s = 0; s < liveSlots.length; s++) {
        syncBogosSlotAttributes(liveSlots[s], nextSlots[s]);
      }

      /* Gift promo teaser suppress state (hidden attr + inline style with
         display:none) is Liquid-owned: suppress_promo = threshold met. It
         must track the incoming HTML on granular settles too, or a cart
         that drops back UNDER the threshold via a qty decrease keeps the
         teaser suppressed forever (nothing else ever clears the attrs).
         Presence mismatch (banner appears/disappears, e.g. gift line
         added) is a structure change — fall through to the full swap. */
      var liveBanner = liveShell.querySelector('.dd-cart-promo-banner:not(.dd-cart-promo-banner--trial)');
      var nextBanner = root.querySelector('.dd-cart-promo-banner:not(.dd-cart-promo-banner--trial)');
      if (!!liveBanner !== !!nextBanner) return false;
      if (liveBanner && nextBanner) {
        if (nextBanner.hasAttribute('hidden')) liveBanner.setAttribute('hidden', '');
        else liveBanner.removeAttribute('hidden');
        var nextBannerStyle = nextBanner.getAttribute('style');
        if (nextBannerStyle == null) liveBanner.removeAttribute('style');
        else if (liveBanner.getAttribute('style') !== nextBannerStyle) liveBanner.setAttribute('style', nextBannerStyle);
      }
      return true;
    } catch (err) {
      console.warn('[cart-paint] granular settle failed — falling back to swap', err);
      return false;
    }
  }

  function patchDrawerFromSection(rawDrawerSectionHtml) {
    var m = metrics();
    if (!rawDrawerSectionHtml) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    var liveRoot = document.getElementById('CartDrawer');
    if (!liveRoot) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    var doc = new DOMParser().parseFromString(rawDrawerSectionHtml, 'text/html');
    var nextRoot = doc.querySelector('#CartDrawer');
    if (!nextRoot) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    var liveItemsShell = liveRoot.querySelector('#CartDrawer-Items');
    var nextItemsShell = nextRoot.querySelector('#CartDrawer-Items');
    if (!liveItemsShell || !nextItemsShell) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    _sectionReplaceInProgress = true;
    var swapToken = ++_drawerItemsSwapToken;
    var delayMs = 0;
    var swapDoneResolve = null;
    var swapDone = new Promise(function (resolve) {
      swapDoneResolve = resolve;
    });
    _pendingDrawerSwapPromise = swapDone;

    var liveCustomCard = liveItemsShell.querySelector('.dd-custom-card');
    var nextHasCustomCard = !!nextItemsShell.querySelector('.dd-custom-card');
    if (liveCustomCard && !nextHasCustomCard) {
      liveCustomCard.classList.add('is--exit');
      delayMs = Math.max(delayMs, CUSTOM_INFO_EXIT_MS);
    }

    var liveXsellAdded = liveItemsShell.querySelector('.ktip-xsell.is--added');
    if (liveXsellAdded) delayMs = Math.max(delayMs, XSELL_COLLAPSE_MS);

    var nextItemsHtml = nextItemsShell.innerHTML;
    /* Wave C: the pre-swap guest-row sanitizer is gone. The dual-render
       contract (snippets/dd-cart-item-price.liquid) makes section HTML
       identical for every session — CSS picks money vs login row — so
       guest-gated markup can no longer arrive here. Canary: three personas
       (logged-in / guest / color-ring guest) green 2026-09-03. */

    function finishItemsSwap() {
      /* Superseded by a newer patch: do NOT touch _sectionReplaceInProgress.
         The newer patch's own finishItemsSwap owns the mutex now; a stale
         callback clearing it here would let a third patch start concurrently
         and interleave DOM writes with the in-flight newer swap. */
      if (_drawerItemsSwapToken !== swapToken) {
        if (swapDoneResolve) swapDoneResolve();
        return;
      }
      try {
        if (liveItemsShell.isConnected) {
          /* Equality gate: skip the wholesale innerHTML swap when the
             server returned the same markup we already have. This is
             the dominant case on cold drawer-opens (no real mutation
             between opens) — without the gate every reopen flashes the
             items list as the browser re-parses, re-renders, and
             re-runs IntersectionObservers / lazy-load handlers on
             identical markup. With the gate, opens with no change are
             true no-ops. */
          if (liveItemsShell.innerHTML !== nextItemsHtml) {
            var settledGranularly = false;
            if (getActiveLineAction()) {
              settledGranularly = tryGranularItemsSettle(liveItemsShell, nextItemsHtml);
              if (settledGranularly) {
                m.drawerGranularSettle = (m.drawerGranularSettle || 0) + 1;
              }
            }
            if (!settledGranularly) {
              liveItemsShell.innerHTML = nextItemsHtml;
            }
            /* Rehydrate on BOTH paths: the granular settle syncs the gift
               slot's Liquid flags (syncBogosSlotAttributes) but the card
               markup itself is client-rendered — dd-cart-gift must re-run
               renderSlot against the fresh flags or a previously hidden
               card stays hidden until the next full swap. */
            rehydrateBogosVisibility();
            emitItemsSwapped('drawer-items');
          }
          /* Wave C: post-swap guest-row repair deleted — dual-render section
             HTML cannot carry guest-gated money rows (see note above). */
        }
      } catch (err) {
        console.error('[cart-paint] finishItemsSwap error', err);
      } finally {
        /* Always release the mutex (even on exception) and drain the queue.
           Only the NEWEST queued HTML is applied — the queue is fed by the
           serialized cart-queue, so entries arrive in mutation order and
           intermediate states are safely superseded by the last one. */
        _sectionReplaceInProgress = false;
        if (_sectionReplaceQueue.length > 0) {
          var nextHtml = _sectionReplaceQueue.pop();
          _sectionReplaceQueue = [];
          window.setTimeout(function () { patchDrawerFromSection(nextHtml); }, 0);
        }
        if (swapDoneResolve) swapDoneResolve();
      }
    }

    if (delayMs > 0) window.setTimeout(finishItemsSwap, delayMs);
    else finishItemsSwap();

    var blockedGuestFooterDowngrade = false;
    var liveFooter = liveRoot.querySelector('.dd-drawer-footer');
    var nextFooter = nextRoot.querySelector('.dd-drawer-footer');
    if (liveFooter && nextFooter) {
      var authSaysCustomer = document.documentElement.getAttribute('data-glass-customer') === 'true';
      var liveCheckoutCta = liveFooter.querySelector('[data-dd-checkout]') || liveFooter.querySelector('#CartDrawer-Checkout');
      var nextCheckoutCta = nextFooter.querySelector('[data-dd-checkout]') || nextFooter.querySelector('#CartDrawer-Checkout');
      var nextGuestCheckoutCta = nextFooter.querySelector('.dd-pay-now--login, .dd-login-btn[href*="/account/login"]');
      var liveFooterLooksCustomer = !!(
        liveCheckoutCta &&
        !liveFooter.querySelector('.dd-pay-now--login, .dd-login-btn[href*="/account/login"]')
      );
      var shouldBlockGuestFooterDowngrade = !!(
        (authSaysCustomer || liveFooterLooksCustomer || liveHadCustomerPriceRows) &&
        liveCheckoutCta &&
        !nextCheckoutCta &&
        nextGuestCheckoutCta
      );
      if (shouldBlockGuestFooterDowngrade) {
        blockedGuestFooterDowngrade = true;
        m.drawerGuestFooterDowngradeBlocked = (m.drawerGuestFooterDowngradeBlocked || 0) + 1;
      }

      var liveSummary = liveFooter.querySelector('[data-dd-total-discount]');
      var nextSummary = nextFooter.querySelector('[data-dd-total-discount]');
      if (liveSummary && nextSummary) {
        liveSummary.setAttribute('data-dd-total-discount', String(nextSummary.getAttribute('data-dd-total-discount') || '0'));
      }

      var liveDisc = liveFooter.querySelector('[data-dd-cart-discounts]');
      var nextDisc = nextFooter.querySelector('[data-dd-cart-discounts]');
      if (liveDisc && nextDisc) {
        var nextResolvedName = nextDisc.getAttribute('data-dd-resolved-discount-name') || '';
        liveDisc.setAttribute('data-dd-resolved-discount-name', nextResolvedName);
        /* Equality gate matches the subtotal-block / checkout-block
           gates a few lines below. Without it, every cold drawer
           open shimmers the discount strip even when nothing changed
           — and any client-rendered chips (e.g. SF-API gift cards
           injected by `dd-storefront-cart.js`) get wiped only to be
           re-injected ~50ms later, producing a visible flicker. */
        if (liveDisc.innerHTML !== nextDisc.innerHTML) {
          withLocalShimmer(liveDisc, function () { liveDisc.innerHTML = nextDisc.innerHTML; });
          if (window.ddDiscountChips && typeof window.ddDiscountChips.render === 'function') window.ddDiscountChips.render();
        }
      }

      var liveSavings = liveFooter.querySelector('[data-dd-savings]');
      var nextSavings = nextFooter.querySelector('[data-dd-savings]');
      if (liveSavings && nextSavings) {
        /* Skip the shimmer when the savings number + display state
           are unchanged. We compare the actual stable inputs (style
           display + money textContent) rather than full innerHTML
           because innerHTML can carry whitespace differences between
           SSR renders that wouldn't actually change the visible
           output. */
        var liveSavingsMoney = liveSavings.querySelector('[data-dd-savings-money]');
        var nextSavingsMoney = nextSavings.querySelector('[data-dd-savings-money]');
        var savingsDisplayChanged = liveSavings.style.display !== nextSavings.style.display;
        var savingsMoneyChanged = liveSavingsMoney && nextSavingsMoney
          && liveSavingsMoney.textContent !== nextSavingsMoney.textContent;
        if (savingsDisplayChanged || savingsMoneyChanged) {
          withLocalShimmer(liveSavings, function () {
            liveSavings.style.display = nextSavings.style.display;
            if (liveSavingsMoney && nextSavingsMoney) liveSavingsMoney.textContent = nextSavingsMoney.textContent;
          });
        }
      }

      /* Totals: replace [data-dd-totals-core] wholesale (Subtotal row + Total-due
         row) from the section HTML. The section's Total-due money is cart.total_price
         (the no-gift-card value); if a gift card is applied, dd-storefront-cart.js
         re-overrides [data-dd-subtotal-money] to the post-gift-card amount on its
         hydrate (which runs right after this on cart:updated). The savings node
         lives inside core, so the earlier [data-dd-savings] patch re-runs against
         the freshly-swapped node and no-ops. */
      var liveTotalsCore = liveFooter.querySelector('[data-dd-totals-core]');
      var nextTotalsCore = nextFooter.querySelector('[data-dd-totals-core]');
      if (liveTotalsCore && nextTotalsCore) {
        /* Keep the total-cents stamp fresh. It lives as an ATTRIBUTE on the
           core container, so an innerHTML-only swap would leave it stale —
           and dd-storefront-cart.js computes "Total due = total_price − gift
           card" from this stamp, so a stale value snaps the total back to its
           pre-mutation amount (e.g. toggling protection appears to do nothing
           when a gift card is applied). */
        var nextTotalCents = nextTotalsCore.getAttribute('data-dd-total-cents');
        if (nextTotalCents != null) {
          liveTotalsCore.setAttribute('data-dd-total-cents', nextTotalCents);
        }
        if (!shouldBlockGuestFooterDowngrade && liveTotalsCore.innerHTML !== nextTotalsCore.innerHTML) {
          withLocalShimmer(liveTotalsCore, function () {
            liveTotalsCore.innerHTML = nextTotalsCore.innerHTML;
          });
        }
      } else {
        /* Pre-breakdown fallback (no totals-core in DOM): patch the subtotal block
           or money node directly, as before. */
        var liveSubtotalBlock = liveFooter.querySelector('[data-dd-subtotal-block]');
        var nextSubtotalBlock = nextFooter.querySelector('[data-dd-subtotal-block]');
        if (liveSubtotalBlock && nextSubtotalBlock) {
          if (!shouldBlockGuestFooterDowngrade && liveSubtotalBlock.innerHTML !== nextSubtotalBlock.innerHTML) {
            withLocalShimmer(liveSubtotalBlock, function () {
              liveSubtotalBlock.innerHTML = nextSubtotalBlock.innerHTML;
            });
          }
        } else {
          var liveSubtotal = liveFooter.querySelector('[data-dd-subtotal-money]');
          var nextSubtotal = nextFooter.querySelector('[data-dd-subtotal-money]');
          if (liveSubtotal && nextSubtotal && liveSubtotal.textContent !== nextSubtotal.textContent) {
            var subtotalHost = liveSubtotal.closest('[data-dd-subtotal]') || liveSubtotal.parentElement;
            withLocalShimmer(subtotalHost, function () { liveSubtotal.textContent = nextSubtotal.textContent; });
          }
        }
      }

      var liveCheckoutBlock = liveFooter.querySelector('[data-dd-checkout-cta-block]');
      var nextCheckoutBlock = nextFooter.querySelector('[data-dd-checkout-cta-block]');
      if (liveCheckoutBlock && nextCheckoutBlock) {
        if (!shouldBlockGuestFooterDowngrade && liveCheckoutBlock.innerHTML !== nextCheckoutBlock.innerHTML) {
          withLocalShimmer(liveCheckoutBlock, function () {
            liveCheckoutBlock.innerHTML = nextCheckoutBlock.innerHTML;
          });
        }
        if (shouldBlockGuestFooterDowngrade && liveCheckoutCta) {
          var hasMerchItems = !!nextItemsShell.querySelector('[data-dd-cart-item]');
          if (hasMerchItems) liveCheckoutCta.removeAttribute('disabled');
          else liveCheckoutCta.setAttribute('disabled', '');
        }
      } else {
        var liveCheckout = liveFooter.querySelector('[data-dd-checkout]') || liveFooter.querySelector('#CartDrawer-Checkout');
        var nextCheckout = nextFooter.querySelector('[data-dd-checkout]') || nextFooter.querySelector('#CartDrawer-Checkout');
        if (liveCheckout && nextCheckout) {
          if (nextCheckout.hasAttribute('disabled')) liveCheckout.setAttribute('disabled', '');
          else liveCheckout.removeAttribute('disabled');
        }
      }

      /* Protection widget lives in .dd-footer-details (not totals-core). Granular
         patches never replaced it, so SSR price could stick at a stale tier on
         mobile until a full reconcile ran. Sync price + merch gate + line keys
         from section HTML; do NOT touch toggle checked / is--active (visual
         persistence contract in dd-shipping-protection.js). */
      var liveProt = liveFooter.querySelector('[data-dd-protection]');
      var nextProt = nextFooter.querySelector('[data-dd-protection]');
      if (liveProt && nextProt) {
        liveProt.classList.toggle('is--has-merch', nextProt.classList.contains('is--has-merch'));
        var previewRaw = nextProt.getAttribute('data-dd-protection-preview-cents');
        if (previewRaw != null && previewRaw !== '') {
          liveProt.setAttribute('data-dd-protection-preview-cents', previewRaw);
          applyProtectionPreviewFromWidget(liveProt);
        }
        var liveProtToggle = liveProt.querySelector('[data-dd-protection-toggle]');
        var nextProtToggle = nextProt.querySelector('[data-dd-protection-toggle]');
        if (liveProtToggle && nextProtToggle) {
          if (nextProtToggle.dataset.variantId) liveProtToggle.dataset.variantId = nextProtToggle.dataset.variantId;
          else delete liveProtToggle.dataset.variantId;
          if (nextProtToggle.dataset.lineKey) liveProtToggle.dataset.lineKey = nextProtToggle.dataset.lineKey;
          else delete liveProtToggle.dataset.lineKey;
        }
      }
    }

    var liveEmpty = liveRoot.querySelector('[data-dd-cart-empty]');
    var nextEmpty = nextRoot.querySelector('[data-dd-cart-empty]');
    if (liveEmpty && nextEmpty) liveEmpty.style.display = nextEmpty.style.display;

    var liveContent = liveRoot.querySelector('[data-dd-cart-content]');
    var nextContent = nextRoot.querySelector('[data-dd-cart-content]');
    if (liveContent && nextContent) liveContent.style.display = nextContent.style.display;

    var liveDrawerHost = document.querySelector('cart-drawer');
    var nextDrawerHost = doc.querySelector('cart-drawer');
    if (liveDrawerHost && nextDrawerHost) {
      if (nextDrawerHost.classList.contains('is-empty')) liveDrawerHost.classList.add('is-empty');
      else liveDrawerHost.classList.remove('is-empty');
    }

    m.drawerPatchSuccess = (m.drawerPatchSuccess || 0) + 1;
    /* If we blocked a guest-footer downgrade, the section footer payload was
       contradictory to known auth state. Mark footer stale so dd-mini-cart can
       repaint subtotal/price from authoritative cart JSON on this mutation. */
    if (blockedGuestFooterDowngrade) _s.sectionFooterFreshTs = 0;
    else _s.sectionFooterFreshTs = Date.now();
    return true;
  }

  function applyFooterFromCart(drawer, cart) {
    if (!drawer || !cart) return;
    updateDrawerFooterMeta(drawer, cart);
  }

  function consumeGranularFooterPending() {
    var v = _s.granularFooterPending;
    _s.granularFooterPending = false;
    return v;
  }

  function normalizeSectionList(sections) {
    if (!sections) return [];
    if (Array.isArray(sections)) return sections.filter(Boolean);
    if (typeof sections === 'string') {
      return sections.split(',').map(function (s) { return String(s || '').trim(); }).filter(Boolean);
    }
    return [];
  }

  function hasAllRequestedSections(payload, ids) {
    if (!payload || typeof payload !== 'object') return false;
    for (var i = 0; i < ids.length; i++) {
      if (typeof payload[ids[i]] !== 'string' || !payload[ids[i]]) return false;
    }
    return true;
  }

  function fetchSectionsBySectionId(ids) {
    var unique = {};
    var list = [];
    for (var i = 0; i < ids.length; i++) {
      var id = String(ids[i] || '').trim();
      if (!id || unique[id]) continue;
      unique[id] = true;
      list.push(id);
    }
    if (!list.length) return Promise.resolve(null);

    var tasks = list.map(function (id) {
      var basePath = id === 'cart-drawer' || id === 'cart-icon-bubble' ? '/' : '/cart';
      var url = basePath + '?section_id=' + encodeURIComponent(id);
      var f = facade();
      if (f.recordRequest) f.recordRequest(url, 'sections-fallback-section-id', 'queue');
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('Section fetch failed (' + r.status + '): ' + id);
          return r.text();
        })
        .then(function (html) { return { id: id, html: html || '' }; });
    });

    return Promise.all(tasks).then(function (rows) {
      var out = {};
      for (var i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].html) out[rows[i].id] = rows[i].html;
      }
      return Object.keys(out).length ? out : null;
    });
  }

  function fetchSectionsFallback(sectionIds) {
    var ids = normalizeSectionList(sectionIds);
    if (!ids.length) return Promise.resolve(null);
    var base = '/cart';
    var url = base + '?sections=' + encodeURIComponent(ids.join(','));
    var f = facade();
    if (f.recordRequest) f.recordRequest(url, 'sections-fallback', 'queue');
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Sections fallback failed (' + r.status + ')');
        return r.json();
      })
      .then(function (payload) {
        if (payload && typeof payload === 'object' && hasAllRequestedSections(payload, ids)) return payload;
        return fetchSectionsBySectionId(ids);
      })
      .catch(function () { return fetchSectionsBySectionId(ids); });
  }

  function parseSectionHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  function captureCartScrollSnapshot() {
    var main = document.getElementById('main-cart-items');
    var mainContents = main ? main.querySelector('.js-contents') : null;
    var drawerList = document.querySelector('[data-dd-cart-items]');
    return {
      winY: window.scrollY || window.pageYOffset || 0,
      drawerScroll: drawerList ? drawerList.scrollTop : 0,
      mainScroll: mainContents ? mainContents.scrollTop : 0
    };
  }

  function restoreCartScrollSnapshot(snap) {
    if (!snap) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.scrollTo(0, snap.winY || 0);
        var drawerList = document.querySelector('[data-dd-cart-items]');
        if (drawerList && typeof snap.drawerScroll === 'number') drawerList.scrollTop = snap.drawerScroll;
        var main = document.getElementById('main-cart-items');
        var mainContents = main ? main.querySelector('.js-contents') : null;
        if (mainContents && typeof snap.mainScroll === 'number') mainContents.scrollTop = snap.mainScroll;
      });
    });
  }

  var DISCOUNT_ICON_SVG =
    '<svg viewBox="0 0 448 512" width="12" height="12" aria-hidden="true"><path d="M48 32H197.5C214.5 32 230.7 38.74 242.7 50.75L418.7 226.7C443.7 251.7 443.7 292.3 418.7 317.3L285.3 450.7C260.3 475.7 219.7 475.7 194.7 450.7L18.75 274.7C6.743 262.7 0 246.5 0 229.5V80C0 53.49 21.49 32 48 32zM112 176C129.7 176 144 161.7 144 144C144 126.3 129.7 112 112 112C94.33 112 80 126.3 80 144C80 161.7 94.33 176 112 176z" fill="currentColor"/></svg>';

  function lineDiscountAllocations(item) {
    if (!item) return [];
    /* Checkout-mirror: line-level (product) discounts only — e.g. Inner Circle.
       Order-level codes are cart-level allocations shown as separate totals rows,
       not per line. Mirrors snippets/dd-cart-item*.liquid, which read
       line_level_discount_allocations exclusively. Reading the all-inclusive
       discount_allocations here would fold order codes into the line price and
       disagree with the SSR drawer. */
    return Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
  }

  function sumDiscountAllocationCents(allocations) {
    var s = 0;
    for (var i = 0; i < allocations.length; i++) {
      var n = Number((allocations[i] && allocations[i].amount) || 0);
      if (isFinite(n)) s += n;
    }
    return s;
  }

  /* ── THE line-price formula ──────────────────────────────────────────
     Mirrors snippets/dd-cart-item-price.liquid EXACTLY. One function feeds
     both the settle renderer (renderLinePriceRowInner) and the optimistic
     predictor (predictLineItem) so JS can never disagree with SSR.

       sale    = max(0, original_line_price − Σ line_level_discount_allocations)
       compare = compare_at × qty (only when compare_at > unit price),
                 else original_line_price
       order-level discount codes NEVER fold into the line.
       $0 final (BOGO/free-gift) renders at $0 regardless of allocations —
       gift discounts may be allocated at order level, so we honor the
       server's final_line_price for the zero case (Liquid's is_bogos_gift
       branch does the same thing).

     For qtyOverride ≠ item.quantity (prediction), line values scale
     per-unit. Flat-% allocations (Inner Circle) scale exactly; tier/qty-
     break misses self-correct at settle and bump the prediction-miss
     telemetry in __ddCartShared. */
  function computeLinePricing(item, qtyOverride) {
    var baseQty = Number(item.quantity || 0);
    var qty = typeof qtyOverride === 'number' ? qtyOverride : baseQty;
    var allocs = lineDiscountAllocations(item);
    var allocSum = sumDiscountAllocationCents(allocs);
    var unitOriginal = typeof item.original_price === 'number' ? item.original_price : Number(item.price || 0);
    var unitAlloc = baseQty > 0 ? allocSum / baseQty : 0;

    var sale;
    var finalLine = item.final_line_price != null ? Number(item.final_line_price) : null;
    if (finalLine === 0 && unitOriginal > 0) {
      /* free-gift line: server says $0 — honor it at any qty */
      sale = 0;
    } else {
      sale = Math.max(0, Math.round((unitOriginal - unitAlloc) * qty));
    }

    var compare = Math.round(unitOriginal * qty);
    var compareAt = item.variant && typeof item.variant.compare_at_price === 'number'
      ? item.variant.compare_at_price : 0;
    var unitPrice = item.variant && typeof item.variant.price === 'number'
      ? item.variant.price : Number(item.price || 0);
    if (compareAt > unitPrice) compare = Math.round(compareAt * qty);

    return {
      qty: qty,
      saleCents: sale,
      compareCents: compare,
      discounted: compare > sale,
      allocations: allocs
    };
  }

  function renderPriceRowInnerFromPricing(pricing, item) {
    var f_ = facade();
    var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };
    var escHtml = f_.escHtml || function (s) { return String(s || ''); };
    var parts = [];
    if (pricing.discounted) {
      parts.push('<span class="dd-cart-item__price dd-cart-item__price--original">' + formatMoney(pricing.compareCents) + '</span>');
      parts.push('<span class="dd-cart-item__price dd-cart-item__price--sale">' + formatMoney(pricing.saleCents) + '</span>');
    } else {
      parts.push('<span class="dd-cart-item__price">' + formatMoney(pricing.saleCents) + '</span>');
    }
    var isGiftZero = item && item.final_line_price === 0 && (item.original_line_price || 0) > 0;
    if (!isGiftZero) {
      for (var i = 0; i < pricing.allocations.length; i++) {
        var a = pricing.allocations[i];
        var title = a.discount_application && a.discount_application.title ? a.discount_application.title : '';
        parts.push(
          '<span class="dd-cart-item__discount-badge">' + DISCOUNT_ICON_SVG +
          '<span class="dd-cart-item__discount-badge__label">' + escHtml(title) + '</span></span>'
        );
      }
    }
    return parts.join('');
  }

  function renderLinePriceRowInner(item) {
    var f_ = facade();
    var isProtectionLine = f_.isProtectionLine || function () { return false; };
    if (isProtectionLine(item)) {
      var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };
      var o = item.original_line_price || item.final_line_price || 0;
      return '<span class="dd-cart-item__price">' + formatMoney(o) + '</span>';
    }
    return renderPriceRowInnerFromPricing(computeLinePricing(item), item);
  }

  /* ── Optimistic line prediction + snapshot/rollback ───────────────────── */

  function predictLineItem(item, newQty) {
    var pricing = computeLinePricing(item, newQty);
    return {
      qty: pricing.qty,
      saleCents: pricing.saleCents,
      compareCents: pricing.compareCents,
      discounted: pricing.discounted,
      html: renderPriceRowInnerFromPricing(pricing, item)
    };
  }

  function findMoneyRow(row) {
    return (
      row.querySelector('.dd-cart-item__price-row--money') ||
      row.querySelector('.dd-cart-item__price-row')
    );
  }

  /* Paint a line's qty (and optionally its money row) in every surface that
     renders it (drawer + cart page share the data-line-key contract).
     opts.paintPrice=false → qty only (guest mode). Returns painted count. */
  function paintLineFromItem(lineKey, item, qty, opts) {
    opts = opts || {};
    var painted = 0;
    var rows = document.querySelectorAll('[data-dd-cart-item][data-line-key="' + (window.CSS && CSS.escape ? CSS.escape(lineKey) : lineKey) + '"]');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var input = row.querySelector('[data-dd-qty-input]');
      if (input && document.activeElement !== input) {
        input.value = String(qty);
        input.setAttribute('value', String(qty));
      }
      if (opts.paintPrice !== false) {
        var moneyRow = findMoneyRow(row);
        var rowShowsGuestGate = moneyRow
          ? (!!moneyRow.querySelector('.dd-cart-item__login-price') || /login to see price/i.test(moneyRow.textContent || ''))
          : false;
        if (moneyRow && !rowShowsGuestGate) {
          moneyRow.innerHTML = typeof opts.priceHtml === 'string'
            ? opts.priceHtml
            : renderPriceRowInnerFromPricing(computeLinePricing(item, qty), item);
        }
        /* cart page desktop total column */
        var totalCol = row.querySelector('.dd-cart-page-item__total-col .dd-cart-page-item__money');
        if (totalCol) {
          var f_ = facade();
          var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };
          var pricing = computeLinePricing(item, qty);
          totalCol.innerHTML = pricing.discounted
            ? '<s class="dd-cart-page-item__total--compare">' + formatMoney(pricing.compareCents) + '</s>' +
              '<span class="dd-cart-page-item__total--sale">' + formatMoney(pricing.saleCents) + '</span>'
            : '<span>' + formatMoney(pricing.saleCents) + '</span>';
        }
      }
      painted += 1;
    }
    return painted;
  }

  /* Snapshot from the last SERVER-COMMITTED cart (never from optimistic DOM).
     Versioned by pending-action id: restore is a no-op when a newer action
     has superseded the snapshot. */
  function captureLineSnapshot(lineKey) {
    var q = queue();
    var cart = q.getLatestCart ? q.getLatestCart() : null;
    if (!cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i] && cart.items[i].key === lineKey) {
        var active = getActiveLineAction();
        return {
          lineKey: lineKey,
          item: cart.items[i],
          actionId: active ? active.id : null,
          ts: Date.now()
        };
      }
    }
    return null;
  }

  function restoreLineSnapshot(snap) {
    if (!snap || !snap.item) return false;
    var active = getActiveLineAction();
    if (active && snap.actionId != null && active.id > snap.actionId) return false;
    return paintLineFromItem(snap.lineKey, snap.item, snap.item.quantity, { paintPrice: true }) > 0;
  }

  function cartSavingsCents(cart) {
    if (cart && typeof cart.total_discount === 'number') return Math.max(0, Number(cart.total_discount) || 0);
    return 0;
  }

  function getAutoDiscountTitle(cart) {
    var allApps = cart.discount_applications || cart.cart_level_discount_applications || [];
    for (var i = 0; i < allApps.length; i++) {
      var app = allApps[i];
      if (app && (app.type === 'automatic' || app.type === 'script' || app.type === 'manual') && app.title) return app.title;
    }
    try {
      var custId = document.documentElement.getAttribute('data-glass-customer-id') || '0';
      var cached = sessionStorage.getItem('dd_regios_name_' + custId);
      if (cached) return cached;
    } catch (e) {}
    return null;
  }

  function renderCartLevelDiscountsHtml(cart) {
    var f_ = facade();
    var escHtml = f_.escHtml || function (s) { return String(s || ''); };
    var escAttr = f_.escAttr || function (s) { return String(s || ''); };

    /* Checkout-mirror fallback (rare /cart.js path when the section footer is
       not fresh). Mirrors the SSR in cart-drawer.liquid / main-cart-footer.liquid:
       (1) the auto/VIP account-tier chip (Inner Circle / Pro — line-level via the
       Regios Script, so it lives in cart.discount_applications, NOT in
       cart_level_discount_applications), then (2) the removable order-level code
       chips. Chips are amount-less (the figure lives on the per-line badge); the
       remove control is only on discount codes. The non-code badge also re-feeds
       renderChips() and the Regios resolver ([data-dd-auto-discount-label]). */
    var out = [];

    var allApps = cart.discount_applications || [];
    for (var k = 0; k < allApps.length; k++) {
      var ad = allApps[k];
      if (!ad) continue;
      var adType = ad.type || '';
      var adTitle = ad.title || '';
      if (adType !== 'discount_code' && adTitle) {
        out.push(
          '<div class="dd-cart-discount dd-cart-discount--auto" data-dd-discount-badge data-discount-code="' + escAttr(adTitle) +
          '" data-discount-type="' + escAttr(adType) + '">' + DISCOUNT_ICON_SVG +
          '<span data-dd-auto-discount-label>' + escHtml(adTitle) + '</span></div>'
        );
        break;
      }
    }

    var apps = cart.cart_level_discount_applications || [];
    for (var j = 0; j < apps.length; j++) {
      var d = apps[j];
      if (!d) continue;
      var title = d.title || '';
      var type = d.type || '';
      var removeBtn = type === 'discount_code'
        ? '<button type="button" class="dd-cart-discount__remove" aria-label="Remove discount ' + escAttr(title) +
          '"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>'
        : '';
      out.push(
        '<div class="dd-cart-discount" data-dd-discount-badge data-discount-code="' + escAttr(title) +
        '" data-discount-type="' + escAttr(type) + '">' + DISCOUNT_ICON_SVG +
        '<span>' + escHtml(title) + '</span>' + removeBtn + '</div>'
      );
    }
    return out.join('');
  }

  function renderCodeChipsHtml(cart) {
    var f_ = facade();
    var escHtml = f_.escHtml || function (s) { return String(s || ''); };
    var escAttr = f_.escAttr || function (s) { return String(s || ''); };

    var allApps = cart.discount_applications || cart.cart_level_discount_applications || [];
    var out = [];
    for (var i = 0; i < allApps.length; i++) {
      var d = allApps[i];
      if (!d || (d.type || '') !== 'discount_code') continue;
      var title = d.title || '';
      out.push(
        '<div class="dd-cart-discount dd-discount-chip" data-dd-discount-chip data-discount-code="' + escAttr(title) + '">' +
        DISCOUNT_ICON_SVG + '<span>' + escHtml(title) + '</span>' +
        '<button type="button" class="dd-cart-discount__remove" aria-label="Remove discount ' + escAttr(title) +
        '"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button></div>'
      );
    }
    return out.join('');
  }

  function updateDrawerFooterMeta(drawer, cart) {
    var f_ = facade();
    var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };

    var summaryEl = drawer.querySelector('[data-dd-total-discount]');
    if (summaryEl) summaryEl.setAttribute('data-dd-total-discount', String(cartSavingsCents(cart) || 0));

    var discHost = drawer.querySelector('[data-dd-cart-discounts]');
    if (discHost) {
      withLocalShimmer(discHost, function () { discHost.innerHTML = renderCartLevelDiscountsHtml(cart); });
    }

    if (window.ddDiscountChips && typeof window.ddDiscountChips.render === 'function') window.ddDiscountChips.render();

    var savingsEl = drawer.querySelector('[data-dd-savings]');
    if (savingsEl) {
      var savingsMoney = savingsEl.querySelector('[data-dd-savings-money]');
      var isCustomer = document.documentElement.getAttribute('data-glass-customer') === 'true';
      var cents = cartSavingsCents(cart);
      withLocalShimmer(savingsEl, function () {
        if (cents > 0 && isCustomer) {
          savingsEl.style.display = '';
          if (savingsMoney) savingsMoney.textContent = formatMoney(cents);
        } else {
          savingsEl.style.display = 'none';
          if (savingsMoney) savingsMoney.textContent = '';
        }
      });
    }

    var compareEls = drawer.querySelectorAll('[data-dd-subtotal-compare]');
    if (compareEls.length) {
      var isCustomerCompare = document.documentElement.getAttribute('data-glass-customer') === 'true';
      var savingsCents = cartSavingsCents(cart);
      var origCents = 0;
      if (savingsCents > 0) {
        if (typeof cart.original_total_price === 'number') origCents = cart.original_total_price;
        else origCents = (Number(cart.total_price) || 0) + savingsCents;
      }
      for (var c = 0; c < compareEls.length; c++) {
        var compareEl = compareEls[c];
        if (savingsCents > 0 && isCustomerCompare && origCents > 0) {
          compareEl.textContent = formatMoney(origCents);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
    }
  }

  function tryApplyGranularMiniCart(cart, opts) {
    opts = opts || {};
    var repairOnly = !!opts.repairOnly;
    var f_ = facade();
    var isProtectionLine = f_.isProtectionLine || function () { return false; };
    var authSaysCustomer = document.documentElement.getAttribute('data-glass-customer') === 'true';

    if (!cart || cart.errors || !Array.isArray(cart.items)) return false;
    var drawer = document.querySelector('cart-drawer[data-dd-cart-shell="mini"]') || document.querySelector('cart-drawer');
    if (!drawer || !drawer.querySelector('[data-dd-cart-items]')) return false;
    var contents = drawer.querySelector('#CartDrawer-CartItems.drawer__contents') || drawer.querySelector('.drawer__contents.js-contents');
    if (!contents) return false;
    var drawerShowsGuestPriceRows = shellHasGuestGatedMoneyRows(contents);
    var drawerShowsCustomerPriceRows =
      !!contents.querySelector('.dd-cart-item__price-row .dd-cart-item__price') &&
      !drawerShowsGuestPriceRows;
    var drawerLooksCustomerCheckout = !!(
      drawer.querySelector('[data-dd-checkout], #CartDrawer-Checkout') &&
      !drawer.querySelector('.dd-pay-now--login, .dd-login-btn[href*="/account/login"]')
    );
    /* Wave C: `forceCustomerPricing` option removed — its only callers were
       the guest-row repair paths, retired with the dual-render contract. */
    var shouldTreatAsCustomerPricing = !!(
      authSaysCustomer ||
      drawerShowsCustomerPriceRows ||
      drawerLooksCustomerCheckout
    );

    var itemByKey = {};
    var lineNumberByKey = {};
    var merchKeys = {};
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      itemByKey[it.key] = it;
      lineNumberByKey[it.key] = i + 1;
      if (!isProtectionLine(it)) merchKeys[it.key] = true;
    }

    var domKeys = {};
    var rows = contents.querySelectorAll('[data-dd-cart-item]');
    for (var r = 0; r < rows.length; r++) {
      var k = rows[r].dataset.lineKey;
      if (k) domKeys[k] = true;
    }

    var missingMerchKey = false;
    for (var mk in merchKeys) {
      if (!domKeys[mk]) {
        missingMerchKey = true;
        if (!repairOnly) return false;
      }
    }

    if (!repairOnly) {
      var groups = contents.querySelectorAll('.dd-item-group');
      for (var g = 0; g < groups.length; g++) {
        var grp = groups[g];
        var ci = grp.querySelector('[data-dd-cart-item]');
        if (!ci || !ci.dataset.lineKey) continue;
        if (!merchKeys[ci.dataset.lineKey]) grp.remove();
      }
    }

    rows = contents.querySelectorAll('[data-dd-cart-item]');
    for (var r2 = 0; r2 < rows.length; r2++) {
      var row = rows[r2];
      var key = row.dataset.lineKey;
      var line = itemByKey[key];
      if (!line) {
        if (!repairOnly) return false;
        continue;
      }
      var lineNumber = lineNumberByKey[key] || 0;
      if (lineNumber > 0) row.setAttribute('data-line', String(lineNumber));

      var keyedNodes = row.querySelectorAll('[data-line-key]');
      for (var kn = 0; kn < keyedNodes.length; kn++) keyedNodes[kn].setAttribute('data-line-key', key);
      var linedNodes = row.querySelectorAll('[data-line]');
      for (var ln = 0; ln < linedNodes.length; ln++) { if (lineNumber > 0) linedNodes[ln].setAttribute('data-line', String(lineNumber)); }

      var input = row.querySelector('[data-dd-qty-input]');
      if (input && !repairOnly) { input.value = String(line.quantity); input.setAttribute('value', String(line.quantity)); }

      var priceRow = findMoneyRow(row);
      var rowShowsGuestPriceGate = false;
      if (priceRow) {
        rowShowsGuestPriceGate =
          !!priceRow.querySelector('.dd-cart-item__login-price') ||
          /login to see price/i.test(priceRow.textContent || '');
      }
      if (priceRow && (!rowShowsGuestPriceGate || shouldTreatAsCustomerPricing)) {
        withLocalShimmer(priceRow, function () { priceRow.innerHTML = renderLinePriceRowInner(line); });
      }
    }
    return !missingMerchKey || repairOnly;
  }

  function replaceCartSections(sections, opts) {
    if (!sections) return Promise.resolve();
    opts = opts || {};
    _s.granularFooterPending = !!opts.skipDrawer;
    var drawerWait = Promise.resolve();

    var snap = captureCartScrollSnapshot();
    var isCartPage = /\/cart\b/.test(window.location.pathname);

    try {
      if (isCartPage) {
        var el1 = document.getElementById('main-cart-items');
        var el2 = document.getElementById('main-cart-footer');
        var didSwapCartPage = false;
        if (el1 && el1.dataset.id && sections[el1.dataset.id]) {
          var target1 = el1.querySelector('.js-contents');
          if (target1) { var html1 = parseSectionHTML(sections[el1.dataset.id], '.js-contents'); if (html1) { target1.innerHTML = html1; didSwapCartPage = true; } }
        }
        if (el2 && el2.dataset.id && sections[el2.dataset.id]) {
          var target2 = el2.querySelector('.js-contents');
          if (target2) {
            var html2 = parseSectionHTML(sections[el2.dataset.id], '.js-contents');
            if (html2) {
              /* The cart-page footer goes through a wholesale `innerHTML`
                 swap (drawer uses granular `patchDrawerFromSection`, but
                 the page-side equivalent doesn't exist yet). That swap
                 nukes ephemeral DOM state the shopper just produced —
                 most visibly the discount/note accordion `is--open`
                 class, the inline `.dd-discount__feedback` success
                 message, the inline cart toast (`[data-dd-cart-toast]`),
                 and any in-progress input the user typed but didn't yet
                 submit. Capture-and-restore around the swap so post-
                 mutation feedback survives the section replace until
                 a proper granular patcher is built. */
              var preserved = captureCartPageFooterEphemera(target2);
              target2.innerHTML = html2;
              restoreCartPageFooterEphemera(target2, preserved);
              didSwapCartPage = true;
            }
          }
        }
        if (didSwapCartPage) emitItemsSwapped('cart-page');
      }

      if (!opts.skipDrawer && sections['cart-drawer']) {
        if (_sectionReplaceInProgress) {
          _sectionReplaceQueue.push(sections['cart-drawer']);
          debugLog('section-replace:queued', { queueLength: _sectionReplaceQueue.length });
        } else {
          var didPatch = patchDrawerFromSection(sections['cart-drawer']);
          if (!didPatch) {
            var m = metrics();
            m.drawerPatchFail = (m.drawerPatchFail || 0) + 1;
            console.error('[cart-paint] patchDrawerFromSection failed in replaceCartSections — patch is the only in-drawer path; investigate DOM mismatch');
          }
        }
        drawerWait = waitForDrawerSwapIdle();
      }

      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        var bubbleHTML = parseSectionHTML(sections['cart-icon-bubble'], '.shopify-section');
        if (bubbleHTML) bubble.innerHTML = bubbleHTML;
      }
    } finally {
      if (opts && opts.scrollToTop) {
        requestAnimationFrame(function () {
          var drawerList = document.querySelector('[data-dd-cart-items]');
          if (drawerList) drawerList.scrollTop = 0;
          var main = document.getElementById('main-cart-items');
          var mainContents = main ? main.querySelector('.js-contents') : null;
          if (mainContents) mainContents.scrollTop = 0;
        });
      } else {
        restoreCartScrollSnapshot(snap);
      }
    }
    return drawerWait;
  }

  window._ddCartPaint = {
    withLocalShimmer: withLocalShimmer,
    rehydrateBogosVisibility: rehydrateBogosVisibility,
    replaceDrawerInnerHTML: replaceDrawerInnerHTML,
    replaceCartDrawerRootHTML: replaceCartDrawerRootHTML,
    parseCartDrawerRootInnerHtml: parseCartDrawerRootInnerHtml,
    patchDrawerFromSection: patchDrawerFromSection,
    applyFooterFromCart: applyFooterFromCart,
    consumeGranularFooterPending: consumeGranularFooterPending,
    normalizeSectionList: normalizeSectionList,
    fetchSectionsFallback: fetchSectionsFallback,
    fetchSectionsBySectionId: fetchSectionsBySectionId,
    hasAllRequestedSections: hasAllRequestedSections,
    parseSectionHTML: parseSectionHTML,
    captureCartScrollSnapshot: captureCartScrollSnapshot,
    restoreCartScrollSnapshot: restoreCartScrollSnapshot,
    waitForDrawerSwapIdle: waitForDrawerSwapIdle,
    replaceCartSections: replaceCartSections,
    tryApplyGranularMiniCart: tryApplyGranularMiniCart,
    computeLinePricing: computeLinePricing,
    predictLineItem: predictLineItem,
    paintLineFromItem: paintLineFromItem,
    captureLineSnapshot: captureLineSnapshot,
    restoreLineSnapshot: restoreLineSnapshot,
    updateDrawerFooterMeta: updateDrawerFooterMeta,
    renderCodeChipsHtml: renderCodeChipsHtml,
    renderCartLevelDiscountsHtml: renderCartLevelDiscountsHtml,
    /* Freshness window: 60s. The previous 2s value was tuned for the
       "user mutated the cart, painter ran, drawer needs to open
       quickly" case — but it expired so fast that any casual reopen
       (close drawer, browse, reopen 5s later) hit `cart-drawer.js
       openFromCartIcon` cold path, which fetches /cart?sections=…
       and re-runs the painter on byte-identical SSR. With the
       innerHTML equality gates in `patchDrawerFromSection` above the
       repaint is now a true no-op when nothing changed, so the
       freshness window's only remaining job is to skip the *fetch*
       itself. 60s is long enough to cover normal reopen cadence and
       short enough that cross-tab cart changes (rare) catch up
       quickly via the next real user action that calls cartMutate. */
    isSectionFooterFresh: function () { return (Date.now() - _s.sectionFooterFreshTs) < 60000; }
  };
})();
